/**
 * Display formatting for the values core sends.
 *
 * Two rules hold everything here together:
 *
 *   - money is integer cents (canon `docs/ARCHITECTURE.md` § API style) and is never
 *     divided anywhere except in `formatMoney`, so a price cannot be rounded two ways on
 *     two surfaces;
 *   - a display date is a **UTC calendar date** (canon § 8), so it is formatted in UTC.
 *     Formatting `2026-08-24` in the visitor's zone would show 23 August to anyone west of
 *     Greenwich, and they would be reading a different board than the one they are on.
 */

const CENTS_PER_UNIT = 100;

/**
 * USD, with cents shown only when there are any: `$79`, `$79.50`, `$1`.
 *
 * A board of whole-dollar prices reads as a column of numbers rather than a column of
 * `.00`, and the amounts that carry cents keep them.
 */
export function formatMoney(cents: number): string {
  const hasFraction = cents % CENTS_PER_UNIT !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(cents / CENTS_PER_UNIT);
}

/** `72% off`. Core derives the percentage; this only words it. */
export function formatPercentOff(percent: number): string {
  return `${String(percent)}% off`;
}

/** Turns a `YYYY-MM-DD` display date into a `Date` at UTC midnight. */
export function parseDisplayDate(displayDate: string): Date | null {
  const parsed = Date.parse(`${displayDate}T00:00:00.000Z`);
  return Number.isNaN(parsed) ? null : new Date(parsed);
}

const LONG_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const SHORT_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/** `Monday 24 August 2026`. Used in prose. */
export function formatLongDate(displayDate: string): string {
  const date = parseDisplayDate(displayDate);
  return date === null ? displayDate : LONG_DATE.format(date).replace(",", "");
}

/** `24 Aug 2026`. Used in section eyebrows, where the date labels a board. */
export function formatShortDate(displayDate: string): string {
  const date = parseDisplayDate(displayDate);
  return date === null ? displayDate : SHORT_DATE.format(date);
}

/**
 * `24 Aug 2026 – 30 Aug 2026`, or `August 2026` when the range is exactly one calendar
 * month. Core returns both bounds so the site never recomputes a week or month boundary
 * and disagrees with the ranking it is labelling.
 */
export function formatPeriod(start: string, end: string): string {
  const from = parseDisplayDate(start);
  const to = parseDisplayDate(end);
  if (from === null || to === null) return `${start} – ${end}`;

  const isWholeMonth =
    from.getUTCDate() === 1 &&
    from.getUTCMonth() === to.getUTCMonth() &&
    from.getUTCFullYear() === to.getUTCFullYear() &&
    to.getUTCDate() ===
      new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 0)).getUTCDate();

  if (isWholeMonth) {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      month: "long",
      year: "numeric",
    }).format(from);
  }

  return `${formatShortDate(start)} – ${formatShortDate(end)}`;
}

/** `1 click` / `42 clicks`. The unit is spelled out so the number is never bare. */
export function formatClicks(count: number): string {
  return count === 1 ? "1 click" : `${String(count)} clicks`;
}

/**
 * When a build read something, for the as-of line canon `D-015` § 7 requires on the
 * click-ranked lists (`WEB-039`).
 *
 * Always UTC, and says so. The board and the Founding wall carry no such line — canon § 7
 * makes them day-stable, so they are not stale in any sense a buyer would notice — but the
 * click-ranked lists move continuously and may be up to six hours behind the build. Saying
 * when they were measured costs nothing, and a figure presented as current when it is six
 * hours old is the kind of small untruth this board exists not to tell.
 *
 * Returns `null` for an unparseable instant rather than printing a guess.
 */
export function formatMeasuredAt(iso: string): string | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const at = new Date(ms);
  const hours = String(at.getUTCHours()).padStart(2, "0");
  const minutes = String(at.getUTCMinutes()).padStart(2, "0");
  return `${SHORT_DATE.format(at)}, ${hours}:${minutes} UTC`;
}
